<template>
  <div class="quote-photo">
    <div class="quote-photo__wrapp">
      <div
        v-if="author"
        class="quote-photo__author"
        :class="{ 'quote-photo__author--without-image': !author.imageUrl }"
      >
        <div
          v-if="author.imageUrl"
          class="quote-photo__author-avatar"
        >
          <div class="quote-photo__author-image-wrapp">
            <component
              :is="'amp-img'"
              v-if="$isAMP"
              :src="author.imageUrl"
              :alt="author.title"
              height="80"
              layout="fixed"
              width="80"
            />
            <img
              v-else
              :src="author.imageUrl"
              class="quote-photo__author-image"
              :alt="author.title"
            />
          </div>
        </div>
        <div class="quote-photo__author-about">
          <div
            class="quote-photo__author-name"
            v-html="author.title"
          />
          <div
            v-if="author.details"
            class="quote-photo__author-description"
            v-html="author.details"
          />
        </div>
      </div>
      <div
        v-if="author.parsed_text"
        class="quote-photo__content"
      >
        <TheContent
          v-for="item in author.parsed_text"
          :key="item.id"
          :elem="item"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { TAuthor } from '~/types/components/BaseQuotePhoto';
  import TheContent from '~/components/TheContent';

  defineProps<{
    author: TAuthor;
  }>();
</script>

<style lang="scss" scoped>
  .quote-photo {
    font-family: $text-font--1;
    padding-bottom: $spacing-6;
    padding-top: $spacing-8;

    &__wrapp {
      margin: 0 auto;

      @include tablet {
        margin: unset;
      }
    }

    &__author {
      position: relative;
      @include tablet {
        display: flex;
        align-items: center;

        &--without-image {
          padding-left: $spacing-6;
        }
      }
    }

    &__author-avatar {
      margin: 0 auto $spacing-3;
      width: 116px;
      height: 88px;
      position: relative;

      flex-shrink: 0;
      @include tablet {
        margin-right: $spacing-5;
        margin-left: $spacing-1;
        margin-bottom: 0;
        margin-top: $spacing-1;
      }

      &::before {
        position: absolute;
        background-image: url('@/assets/images/svg/avatar-decoration.svg');
        background-size: contain;
        background-repeat: no-repeat;
        content: '';
        width: 116.26px;
        height: 111.55px;
        z-index: 1;
        left: -4px;
        top: -16px;
      }
    }

    &__author-image-wrapp {
      width: 88px;
      height: 88px;
      border: 4px solid white;
      border-radius: 50%;
      overflow: hidden;
      z-index: 2;
      position: relative;
    }

    &__author-image {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    &__author-name {
      color: #000000;
      font-weight: 500;
      @include normalText;
      margin-bottom: 2px;
      text-align: center;

      :deep() a {
        @include link-styles;
      }

      @include tablet {
        @include mediumText;
        text-align: unset;
      }
    }

    &__author-description {
      color: $text-color--1;
      text-align: center;
      @include smallText;
      position: relative;
      :deep() a {
        @include link-styles;
      }

      @include tablet {
        @include normalText;
        text-align: unset;
      }
    }

    &__content {
      margin-top: $spacing-3;
      color: $text-color--2;
      word-break: break-word;
      position: relative;
      @include normalText;
      :deep() a {
        @include link-styles;
      }

      @include tablet {
        @include mediumText;
        margin-top: $spacing-6;
        padding: 0 $spacing-6;
      }
    }
  }
</style>
